import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import './style.scss';
import { t } from '@web-solutions/module-localization';
import { getMonthName } from 'src/onboarding/helpers/getMonthName';

const CustomDot = (props) => {
  const { cx, cy, payload, index, title, difference } = props;

  const labelWeight = props.payload.weight;

  const fillColor =
    index === 0 ? (difference < 1 && difference >= 0 ? '#00CE00' : '#FF693A') : '#00CE00';

  return (
    <>
      <circle cx={cx} cy={cy} r={10} fill={fillColor} stroke='white' strokeWidth={2} />
      <text
        x={index === 1 ? cx - 45 : cx - 10}
        y={cy - 18}
        fontSize='15'
        fontWeight='600'
        fontFamily='Inter'
        fill='black'
      >
        {labelWeight} {title}
      </text>
    </>
  );
};

const CustomTick = ({ x, y, payload, index }) => {
  let dx = 0;

  if (index === 0) {
    dx = 15;
  }

  return (
    <text
      x={x + dx}
      y={y + 10}
      textAnchor='middle'
      fontSize={15}
      fontFamily='Inter'
      fontWeight='600'
      fill='#8A8A8A'
    >
      {payload.value}
    </text>
  );
};

const WeightChart = ({ weightNow, dateWhen, weightWhen, unit, styles }) => {
  const weightData = [
    { date: t('onboarding.goal.now'), weight: weightNow },
    { date: `${getMonthName(dateWhen.month)} ${dateWhen.year}`, weight: weightWhen },
  ];

  const startValue = weightNow;
  const endValue = weightWhen;
  const difference = startValue - endValue;

  const minWeight = Math.min(startValue, endValue);
  const maxWeight = Math.max(startValue, endValue);

  const verticalPaddingPercent = 0.38;

  const range = (maxWeight - minWeight) / (1 - verticalPaddingPercent);
  const minY = minWeight - range * verticalPaddingPercent;
  const maxY = minY + range;

  return (
    <div
      className='containerGraph'
      style={
        styles && { marginTop: '30px', marginBottom: '20px', position: 'relative', width: 343 }
      }
    >
      <ResponsiveContainer width='100%' height={200}>
        <LineChart data={weightData}>
          <XAxis
            dataKey='date'
            tick={CustomTick}
            tickLine={false}
            strokeWidth='1'
            stroke='#EDEDED'
          />

          <YAxis
            tick={false}
            stroke={difference < 1 && difference >= 0 ? '#00CE00' : '#FF693A'}
            strokeWidth='1'
            strokeDasharray='10 10'
            domain={[minY, maxY]}
          />

          <CartesianGrid vertical={false} stroke='#EDEDED' strokeWidth={2} />

          <line
            x1='18%'
            y1={60}
            x2='100%'
            y2={60}
            stroke='#C1C1C1'
            strokeWidth='1'
            opacity='0.3'
            strokeDasharray='3 3'
          />
          <line
            x1='18%'
            y1={110}
            x2='100%'
            y2={110}
            stroke='#C1C1C1'
            opacity='0.3'
            strokeWidth='1'
            strokeDasharray='3 3'
          />
          <line
            x1='18%'
            y1={24}
            x2='100%'
            y2={24}
            stroke='#C1C1C1'
            strokeWidth='1'
            opacity='0.3'
            strokeDasharray='3 3'
          />

          {[0.43, 0.72, 0.987].map((pos, i) => (
            <line
              key={i}
              x1={`${pos * 100}%`}
              y1='10'
              x2={`${pos * 100}%`}
              y2='160'
              stroke={
                i === 2 ? '#00CE00' : difference < 1 && difference >= 0 ? '#00CE00' : '#FF693A'
              }
              strokeWidth='1'
              strokeDasharray='10 10'
            />
          ))}

          <Line
            type='bump'
            strokeWidth={3}
            dataKey='weight'
            dot={false}
            stroke='#EDEDED'
            animationEasing='linear'
            animationBegin={500}
          />

          <Line
            type='bump'
            dataKey='weight'
            strokeWidth={0}
            stroke='url(#gradient)'
            dot={<CustomDot title={unit} difference={difference} />}
            animationEasing='linear'
            animationDuration={300}
          />

          <Line
            type='bump'
            dataKey='weight'
            strokeWidth={3}
            stroke={difference < 1 && difference >= 0 ? '#00CE00' : 'url(#gradient)'}
            dot={<CustomDot title={unit} difference={difference} />}
            animationEasing='linear'
            animationBegin={500}
          />

          <defs>
            <linearGradient id='gradient' x1='0' y1='0' x2='1' y2='0'>
              <stop offset='0%' stopColor='#FF693A' />
              <stop offset='60%' stopColor='#FFEC40' />
              <stop offset='100%' stopColor='#00CE00' />
            </linearGradient>
          </defs>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default WeightChart;
